import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Plus,
  MagnifyingGlass,
  FunnelSimple,
  CircleNotch,
  Phone,
  Envelope,
  MapPin,
  Trash
} from '@phosphor-icons/react';


function ClientStatusBadge({ status }) {
  const styles = {
    'Lead': 'badge-lead',
    'Prospect': 'badge-prospect',
    'Client': 'badge-client',
    'Inactive': 'badge-inactive',
  };

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || 'badge-lead'}`}>
      {status}
    </span>
  );
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, [statusFilter]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API_URL}/api/clients?${params}`, {
        withCredentials: true
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchClients();
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => {
    setSelectedIds(prev => prev.size === clients.length ? new Set() : new Set(clients.map(c => c.id)));
  };
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminar ${selectedIds.size} clientes seleccionados?`)) return;
    try {
      await axios.post(`${API_URL}/api/bulk/delete-clients`, { ids: [...selectedIds] }, { withCredentials: true });
      setSelectedIds(new Set());
      fetchClients();
    } catch (e) { alert('Error al eliminar'); }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clientes</h1>
          <p className="text-slate-500">Gestiona tus clientes y leads</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium" data-testid="bulk-delete-clients">
              <Trash size={18} /> Eliminar ({selectedIds.size})
            </button>
          )}
          <Link to="/clients/new" className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium" data-testid="new-client-button">
            <Plus size={20} weight="bold" /> Nuevo Cliente
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, email, teléfono..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <FunnelSimple size={20} className="text-slate-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              data-testid="status-filter"
            >
              <option value="">Todos los estados</option>
              <option value="Lead">Lead</option>
              <option value="Prospect">Prospecto</option>
              <option value="Client">Cliente</option>
              <option value="Inactive">Inactivo</option>
            </select>
            <button
              type="submit"
              className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
              data-testid="search-button"
            >
              Buscar
            </button>
          </div>
        </form>
      </div>

      {/* Clients List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CircleNotch size={32} className="animate-spin text-blue-600" />
          </div>
        ) : clients.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="clients-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-4 w-10">
                    <input type="checkbox" checked={selectedIds.size === clients.length && clients.length > 0} onChange={toggleAll} className="w-4 h-4 accent-blue-600" data-testid="select-all-clients" />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Cliente
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Contacto
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Ubicación
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Servicio
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {clients.map((client) => (
                  <tr
                    key={client.id}
                    onClick={() => navigate(`/clients/${client.id}`)}
                    className={`table-row-hover cursor-pointer ${selectedIds.has(client.id) ? 'bg-blue-50' : ''}`}
                    data-testid={`client-row-${client.id}`}
                  >
                    <td className="px-3 py-4 w-10">
                      <input type="checkbox" checked={selectedIds.has(client.id)} onChange={(e) => { e.stopPropagation(); toggleSelect(client.id); }} onClick={e => e.stopPropagation()} className="w-4 h-4 accent-blue-600" />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-slate-900">{client.name}</p>
                        {client.company && (
                          <p className="text-sm text-slate-500">{client.company}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Phone size={14} />
                          {client.phone}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Envelope size={14} />
                          {client.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <MapPin size={14} />
                        {client.city ? `${client.city}, ${client.state || ''}` : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {client.service_interest || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <ClientStatusBadge status={client.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <p className="text-slate-500 mb-4">No se encontraron clientes</p>
            <Link
              to="/clients/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              Agregar primer cliente
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
